import { ACCOUNT_ROLES } from "./articleRoles";

const CATEGORY = "Your account";

export const ACCOUNT_ARTICLES = [
   {
      id: "profile-information",
      category: CATEGORY,
      title: "Update your profile information",
      summary: "Edit your name, phone number, location and profile picture.",
      roles: ACCOUNT_ROLES,
      related: ["login-and-security", "delete-or-deactivate-account"],
      body: `
Your profile holds your personal details and your location. For the organization owner it also holds the organization's details. To open it, click your avatar in the top-right corner and choose **Profile**.

## Edit your details

1. Open **Profile** from your avatar menu.
2. Update the fields you need:
   - **Personal information**: **Full Name** and **Phone number** are required. **Gender** is optional. **Role within Organization** is shown for reference and can't be edited.
   - **Location**: **Country**, **State** and **Address line** are required. **City** is required when the state you pick has cities. **District**, **Subdistrict**, **Village/Neighborhood** and **ZIP code** are optional.
   - **Company information**: the organization's name, type and country. Only the organization owner can change these. Everyone else sees them read-only.
3. Click **Save changes**. You'll see "Your information has been updated successfully."

> **Tip:** Changing **Country** clears **State** and **City**, and changing **State** clears **City**. Pick them again before you save.

Your email address isn't on this page. To change it, see [Change your email or password](#login-and-security).

## Change your profile picture

1. Click your picture, or the pencil icon on it, at the top of the page.
2. Drop an image on the box or click the box to choose a file. JPG and PNG files up to 20 MB are accepted.
3. Adjust the square crop and click **Crop**.
4. Click **Change profile picture**.

To remove your picture, click the bin icon (**Delete profile picture**), then click **Change profile picture**.
`
   },
   {
      id: "login-and-security",
      category: CATEGORY,
      title: "Change your email or password",
      summary: "Update the email you sign in with, or set a new password.",
      roles: ACCOUNT_ROLES,
      related: ["profile-information"],
      body: `
Click your avatar in the top-right corner and choose **Login & Security**. Then choose **Update email** or **Update password**.

Both changes are confirmed with a 6-digit code sent by email. Afterwards you are signed out and sign in again with the new details.

## Change your email

1. Choose **Update email**. Your current address is shown under **Email**.
2. Enter the new address in **New email** and again in **Confirm email**.
3. Click **Update**. A 6-digit code is sent to your **new** address.
4. Type the code in the **Confirmation code** window. It submits as soon as you enter the sixth digit.
5. You'll see "Your email has been updated successfully." Sign in again with your new email.

## Change your password

1. Choose **Update password**.
2. Enter your **Old password**, then your new password in **New password** and **Confirm new Password**. The new password needs at least one uppercase letter, one lowercase letter and one number.
3. Click **Update**. A 6-digit code is sent to your current email.
4. Type the code in the **Confirmation code** window.
5. You'll see "Your password has been updated successfully." Sign in again with your new password.

> **Tip:** No code? Check your spam folder, then click **Resend verification code** in the code window.
`
   },
   {
      id: "delete-or-deactivate-account",
      category: CATEGORY,
      title: "Deactivate or delete your account",
      summary: "Take a break from KAPsurvey, or remove your account for good.",
      roles: ACCOUNT_ROLES,
      related: ["profile-information", "switch-organization"],
      body: `
**Deactivating** hides your account until you come back. **Deleting** removes it permanently.

> **Important:** If you are the organization owner, both options apply to the **whole organization**, not only to you. Deleting an organization can't be undone.

## Deactivate or delete

1. Click your avatar and choose **Profile**.
2. Click **Delete or deactivate account**.
3. Choose **Deactivate my account** or **Delete my account**.
4. Pick a reason and fill in the feedback box.
5. Click **Continue**. You are signed out.

## What each option does

| Your role | Deactivate | Delete |
|---|---|---|
| Organization owner | Hides the organization from its members until you sign in again. Your plan keeps renewing and you keep being charged | Permanently deletes the organization and all of its data, and your account unless you own another organization. Your plan ends immediately, with no refund for the rest of the period. Not possible on an annual plan before its 12-month commitment ends, or while an amount is owed |
| Any other role | Removes your access to this organization until you reactivate it | Permanently deletes your user |

Deactivating doesn't stop your payments. To stop paying, cancel your plan from the **Subscription** page.

## Come back after deactivating

- **You belong to one organization:** sign in. Your account is reactivated and you'll see "Your account has been activated!"
- **You belong to several organizations:** sign in and click **Activate account** on that organization's card.
`
   },
   {
      id: "switch-organization",
      category: CATEGORY,
      title: "Switch between organizations",
      summary: "Choose which organization you work in when you belong to more than one.",
      related: ["delete-or-deactivate-account"],
      body: `
If you belong to more than one organization, you choose which one to work in.

1. After you sign in, the **My organizations** page opens. If you belong to only one organization, it opens on its own and you skip this page.
2. Click **See workspaces →** on the organization's card.

To switch later, click **Back to organizations** at the top left of the workspace or **My tasks** page.

## What the cards mean

- **See workspaces →**: the organization is ready to use.
- **Activate account**: you deactivated your access to this organization. Click it to come back.
- **No link**, with the tooltip "This organization has been deactivated.": the organization is inactive or has no active subscription. Contact the organization's owner.
`
   }
];
